/**
 * TableCell Component
 * Encapsulated TableCell with consistent styling
 */

import React from 'react';
import { TableCell as MuiTableCell, TableCellProps as MuiTableCellProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface TableCellProps extends Omit<MuiTableCellProps, 'variant'> {
  variant?: 'header' | 'body';
  stickyRight?: boolean;
}

export const TableCell: React.FC<TableCellProps> = ({ 
  variant = 'body',
  stickyRight = false,
  sx,
  className,
  children,
  ...props 
}) => {
  const theme = useTheme();
  
  const defaultStyles = variant === 'header'
    ? {
        fontWeight: theme.typography.subtitle2.fontWeight,
        color: theme.palette.text.primary,
        backgroundColor: 'transparent',
        borderBottom: `1px solid ${theme.customColors.tableBorder}`,
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      }
    : {
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.customColors.tableBorder}`,
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      };

  const stickyStyles = stickyRight
    ? {
        position: 'sticky',
        right: 0,
        backgroundColor: theme.palette.background.paper,
        zIndex: variant === 'header' ? 3 : 2,
      }
    : undefined;

  const mergedClassName = [className, stickyRight ? 'sticky-table-cell' : null]
    .filter(Boolean)
    .join(' ');

  return (
    <MuiTableCell
      className={mergedClassName || undefined}
      sx={{
        ...defaultStyles,
        ...stickyStyles,
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiTableCell>
  );
};

